<template>
  <v-dialog
    v-model="show"
    max-width="700"
  >
    <v-card>
      <v-card-title class="flex-nowrap justify-space-between align-center">
        <small class="edit-report-dialog-title">{{ dialogTitle }} {{ report.Name || report.Task_Name }}/{{ report.Full_Name || report.Employee_FullName }}</small>
        <v-btn text icon small @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pt-3 pb-0">
        <v-form
          ref="form"
          v-model="isValid"
          lazy-validation
        >
          <v-row v-if="show">
            <v-col cols="4">
              <TimePicker
                dense
                label="Reported Duration"
                v-model="reportedDur"
                :disabled="isPreviewMode || report.Locked"
              />
            </v-col>
            <v-col cols="4">
              <TimePicker
                dense
                label="Paid Duration"
                v-model="paidDur"
                :disabled="isPreviewMode || report.Locked"
              />
            </v-col>
            <v-col cols="4">
              <TimePicker
                dense
                label="Invoice Duration"
                v-model="invoiceDur"
                :disabled="isPreviewMode || report.Locked"
              />
            </v-col>
            <v-col cols="4">
              <DatePicker
                v-model="reportStartDate"
                label="Start Date"
                :disabled="isPreviewMode || report.Locked"
                :rules="isPreviewMode ? [] : rules.required"
                dense
              />
            </v-col>
            <v-col cols="4">
              <v-select
                label="Activity Type"
                v-model="reportActivityType"
                :items="activityTypes"
                item-text="Description"
                item-value="Activity_Type"
                dense
                hide-details
                :rules="isPreviewMode ? [] : rules.required"
                :disabled="isPreviewMode || report.Locked"
              ></v-select>
            </v-col>
            <v-col cols="4" v-if="report.Reporting_ID && !isPreviewMode">
              <v-checkbox
                label="Locked"
                v-model="reportLocked"
                dense
                hide-details
              ></v-checkbox>
            </v-col>
            <v-col cols="12">
              <div class="description-container">
                <v-textarea
                  label="Description"
                  v-model="reportDescription"
                  :rules="isPreviewMode ? [] : rules.required"
                  :disabled="!isPreviewMode && report.Locked"
                  :readonly="isPreviewMode"
                  class="description-preview-selectable"
                ></v-textarea>
                <BadWords
                  v-if="!isPreviewMode"
                  :description="reportDescription"
                  :hours="reportedDur"
                  :bad-words="badWords"
                />
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="isPreviewMode"
          small
          color="primary"
          @click="onSave"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { sharedReportingMixin } from '@/mixins/sharedReportingMixin'

export default {
  name: 'EditReportDialog',
  mixins: [sharedReportingMixin],
  components: {
    TimePicker: () => import('@/components/global/TimePicker.vue'),
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    BadWords: () => import('@/components/global/BadWords.vue')
  },
  props: {
    value: {
      required: true,
      type: Object,
      default () {
        return {
          report: {},
          tasks: [],
          show: false
        }
      }
    }
  },
  data () {
    return {
      isValid: true,
      rules: {
        required: [v => !!v || 'Field is required']
      },
      badWords: []
    }
  },
  computed: {
    isPreviewMode () {
      return this.report && this.report.Status === 'C'
    },
    dialogTitle () {
      if (this.isPreviewMode) return 'View Reporting for'
      return (this.report.Reporting_ID ? 'Edit' : 'Add') + ' Reporting for'
    },
    user () {
      return this.$store.getters.getUser
    },
    reportedDur: {
      get () {
        return this.report.Real_Dur
      },
      set (val) {
        let invoiceDur = this.report.Invoice_Dur
        let paidDur = this.report.Report_Dur
        if (paidDur !== val) {
          paidDur = val
        }
        if (invoiceDur !== val) {
          invoiceDur = val
        }
        const v = { ...this.report, Real_Dur: val, Report_Dur: paidDur, Invoice_Dur: invoiceDur }
        this.report = v
      }
    },
    paidDur: {
      get () {
        return this.report.Report_Dur
      },
      set (val) {
        this.report = { ...this.report, Report_Dur: val }
      }
    },
    invoiceDur: {
      get () {
        return this.report.Invoice_Dur
      },
      set (val) {
        this.report = { ...this.report, Invoice_Dur: val }
      }
    },
    employeeList () {
      return this.$store.getters.getEmployee
    },
    activityTypes () {
      return this.$store.getters.getActivityTypes
    },
    report: {
      get () {
        return { ...this.value.report }
      },
      set (val) {
        const res = {
          report: val,
          show: true,
          tasks: this.value.tasks
        }
        this.$emit('input', res)
      }
    },
    reportActivityType: {
      get () {
        return this.report.Activity_Type
      },
      set (val) {
        this.report = {
          ...this.report,
          Activity_Type: val
        }
      }
    },
    reportStartDate: {
      get () {
        return this.report.Start_Date
      },
      set (val) {
        this.report = {
          ...this.report,
          Start_Date: val
        }
      }
    },
    reportLocked: {
      get () {
        return this.report.Locked
      },
      set (val) {
        this.report = {
          ...this.report,
          Locked: val
        }
      }
    },
    reportDescription: {
      get () {
        return this.report.Description
      },
      set (val) {
        this.report = {
          ...this.report,
          Description: val
        }
      }
    },
    show: {
      get () {
        if (this.value.show) {
          this.$store.dispatch('getEmployeeList')
          this.fetchBadWords()
        }
        return this.value.show
      },
      set (val) {
        const res = {
          report: val ? this.value.report : null,
          show: val,
          tasks: this.value.tasks
        }
        this.$emit('input', res)
      }
    },
    tasks () {
      return this.value ? this.value.tasks || [] : []
    }
  },
  methods: {
    async fetchBadWords () {
      this.badWords = await this.getBadWords()
    },
    setProjectAndEmployeeId () {
      this.$set(this.report, 'Project_ID', this.tasks.filter(t => t.Task_ID === this.report.Task_ID)[0].Project_ID)
      this.$set(this.report, 'Employee_ID', this.tasks.filter(t => t.Task_ID === this.report.Task_ID)[0].Employee_ID)
      this.$emit('input', {
        report: this.report,
        show: true,
        tasks: this.tasks
      })
    },
    onSave () {
      this.$refs.form.validate()
      if (this.isValid) {
        if (this.report.Reporting_ID) {
          this.updateReport()
        } else {
          this.createReport()
        }
      }
    },
    updateReport () {
      this.$emit('updateReport', this.report)
      this.$emit('input', {
        report: this.report,
        show: false
      })
    },
    createReport () {
      this.$emit('createReport', this.report)
      this.$emit('input', {
        report: this.report,
        show: false
      })
    }
  }
}
</script>

<style scoped>
.description-container {
  position: relative;
}
.bad-words-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}

.edit-report-dialog-title {
  word-break: normal;
}
</style>
